/**
 * Auth Store — Zustand
 *
 * Manages authentication state for the app.
 */

import { create } from "zustand";
import { api, setTokens, clearTokens, loadStoredRefreshToken, getAccessToken, refreshAccessToken } from "../lib/api";
import { ws } from "../lib/ws";
import { useCrypto } from "./useCrypto";
import { generateDeviceId } from "./useCrypto-helpers";

interface User {
  id: string;
  email: string;
  displayName: string;
  avatarUrl?: string | null;
  totpEnabled: boolean;
  isAdmin?: boolean;
  roles?: string[];
  orgRole?: "owner" | "admin" | "moderator" | "member" | null;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** E2EE fully initialized (keys + backup restore done) — chat should wait for this */
  e2eeReady: boolean;

  // Actions
  login: (email: string, password: string, totpCode?: string, trustDevice?: boolean) => Promise<void>;
  register: (email: string, displayName: string, password: string, meta?: { website?: string; _t?: number; inviteToken?: string }) => Promise<void>;
  logout: () => Promise<void>;
  restoreSession: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start loading until session check
  e2eeReady: false,

  login: async (email, password, totpCode, trustDevice) => {
    const deviceToken = localStorage.getItem("kp_device_token") ?? undefined;
    const data = await api.post<{
      accessToken: string;
      refreshToken: string;
      user: User;
      deviceToken?: string;
    }>("/auth/login", { email, password, totpCode, trustDevice, deviceToken });

    // Store device token if returned
    if (data.deviceToken) {
      localStorage.setItem("kp_device_token", data.deviceToken);
    }

    setTokens(data.accessToken, data.refreshToken);
    // Set user but DON'T set e2eeReady yet — chat must wait
    set({ user: data.user, isAuthenticated: true, isLoading: false, e2eeReady: false });
    // Initialize E2EE FIRST so deviceId is stable before WS connect
    // (avoids connect → deviceId changes → reconnect churn)
    generateDeviceId(); // ensure fallback exists
    try {
      await useCrypto.getState().initialize();
      if (!useCrypto.getState().initialized) {
        // Device key upload failed — without server-side keys peers can't send
        // to us. Surface the error so the user can retry instead of entering
        // chat with a half-broken E2EE state.
        throw new Error("E2EE-Initialisierung fehlgeschlagen. Bitte erneut versuchen.");
      }
      await useCrypto.getState().initBackupKey(password);
    } catch (err) {
      // Roll back auth state so LoginPage stays mounted and can show the error
      clearTokens();
      set({ user: null, isAuthenticated: false, isLoading: false, e2eeReady: false });
      throw err;
    }
    // NOW connect WS with the final stable deviceId
    void ws.connect();
    set({ e2eeReady: true });
    // F19 backfill — non-blocking. Adds locally-decrypted plaintexts to the
    // server mirror so peers without sender-keys can recover via /plaintext-failsafe.
    void import("../lib/plaintext-backfill").then(({ runPlaintextBackfill }) => {
      // Defer 5s so chat loads first; backfill is a background task.
      setTimeout(() => runPlaintextBackfill().catch(() => {}), 5_000);
    });
  },

  register: async (email, displayName, password, meta) => {
    const data = await api.post<{
      accessToken: string;
      refreshToken: string;
      user: User;
    }>("/auth/register", { email, displayName, password, ...(meta ?? {}) });

    setTokens(data.accessToken, data.refreshToken);
    set({ user: data.user, isAuthenticated: true, isLoading: false, e2eeReady: false });
    // Initialize E2EE FIRST so deviceId is stable before WS connect
    generateDeviceId(); // ensure fallback exists
    try {
      await useCrypto.getState().initialize();
      if (!useCrypto.getState().initialized) {
        throw new Error("E2EE-Initialisierung fehlgeschlagen. Bitte erneut versuchen.");
      }
      await useCrypto.getState().initBackupKey(password);
    } catch (err) {
      clearTokens();
      set({ user: null, isAuthenticated: false, isLoading: false, e2eeReady: false });
      throw err;
    }
    void ws.connect();
    set({ e2eeReady: true });
    // F19 backfill — non-blocking.
    void import("../lib/plaintext-backfill").then(({ runPlaintextBackfill }) => {
      setTimeout(() => runPlaintextBackfill().catch(() => {}), 5_000);
    });
  },

  logout: async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Ignore logout errors
    }
    ws.disconnect();
    clearTokens();
    // Erase all E2EE data on logout
    await useCrypto.getState().eraseAll();
    // Clean URL (remove invite token etc.)
    if (window.location.search || window.location.pathname !== "/") {
      window.history.replaceState({}, "", "/");
    }
    set({ user: null, isAuthenticated: false, isLoading: false, e2eeReady: false });
  },

  restoreSession: async () => {
    const stored = loadStoredRefreshToken();
    if (!stored) {
      set({ isLoading: false });
      return;
    }

    try {
      // F10: Use the shared refreshAccessToken() so concurrent callers (multiple
      // components mounting, multi-tab reloads) share one in-flight refresh rather
      // than each consuming the single-use rotation token independently.
      // If another tab already refreshed and broadcast the new tokens, getAccessToken()
      // returns non-null here and we skip the network call entirely.
      if (!getAccessToken()) {
        await refreshAccessToken();
      }

      const user = await api.get<{ user: User & { lastDeviceId?: string | null } }>("/users/me");
      set({ user: user.user, isAuthenticated: true, isLoading: false, e2eeReady: false });
      // Use server-known deviceId if browser storage was wiped (cookie/localStorage/IDB)
      if (user.user.lastDeviceId && !localStorage.getItem("kollegeplus:deviceId")) {
        localStorage.setItem("kollegeplus:deviceId", user.user.lastDeviceId);
        console.debug("[Auth] Restored deviceId from server:", user.user.lastDeviceId);
      }
      // Initialize E2EE FIRST so deviceId is stable before WS connect
      generateDeviceId(); // ensure fallback exists
      await useCrypto.getState().initialize();
      if (!useCrypto.getState().initialized) {
        // Restored session can't bring up E2EE — drop tokens and force a fresh
        // login rather than parking the user on the loading screen forever.
        throw new Error("E2EE-Initialisierung beim Session-Restore fehlgeschlagen");
      }
      // NOW connect WS with the final stable deviceId
      void ws.connect();
      set({ e2eeReady: true });
      // F19 backfill — non-blocking.
      void import("../lib/plaintext-backfill").then(({ runPlaintextBackfill }) => {
        setTimeout(() => runPlaintextBackfill().catch(() => {}), 5_000);
      });
    } catch (err) {
      console.warn("[Auth] Session restore failed:", err);
      // Only clear tokens on definitive auth failures (401/403 ApiError). Transient
      // failures — network blips, IndexedDB locked by another tab, E2EE init in a
      // throttled background tab — must NOT destroy a valid session, otherwise a
      // background reload (e.g. auto-update) would surprise-logout the user. A later
      // reload / tab refocus retries `restoreSession` with the still-valid tokens.
      const status = (err as { status?: number } | null)?.status;
      if (status === 401 || status === 403) {
        clearTokens();
      }
      set({ user: null, isAuthenticated: false, isLoading: false, e2eeReady: false });
    }
  },
}));
